import type { ApiClient } from '@ghp/api-client'
import { SURVEY_VERSION, type AssignedInquiry } from '@ghp/domain'
import { schematicFingerprint } from '@ghp/schematic'

import type { DataDatabase } from './data-db'
import { latestEntry } from './outbox'

/** Le sous-ensemble injecté du client : la relecture d'un média de l'équipe. */
export type CarriedMediaApi = Pick<ApiClient['sync'], 'downloadMedium'>

/**
 * Range sur la tablette les photos et pièces qu'une contre-visite reprend de la visite précédente
 * (#675), **avant que la fiche ne s'ouvre** : sur place, elle s'ouvre peut-être hors réseau.
 *
 * Le serveur les a déjà dupliquées dans le stockage, sous la clé de l'équipe et un identifiant
 * propre à la contre-visite : la tablette les range comme ses propres pièces, **déjà envoyées**
 * (`uploadedAt`), avec leur provenance (`carriedFrom`). Rien n'est partagé avec le contrôle
 * d'origine — retirer une pièce de la contre-visite ne touche que sa copie.
 *
 * ⚠️ **Tant que la fiche n'est pas ouverte, et seulement alors.** Une fois le brouillon créé, les
 * pièces appartiennent à l'agent : en remettre une qu'il a retirée défairait son geste. Avant, la
 * liste suit la tournée — une pièce que le serveur ne sert plus (la saisie reprise a changé) se
 * retire.
 *
 * ⚠️ **Une pièce qui ne descend pas ne fait pas échouer la descente** : la tournée est déjà rangée,
 * et la passe suivante reprend ce qui manque.
 */
export async function storeCarriedMedia(
  db: DataDatabase,
  api: CarriedMediaApi,
  accessToken: string,
  inquiries: readonly AssignedInquiry[],
): Promise<void> {
  for (const inquiry of inquiries) {
    if (inquiry.followUpSource === null) continue
    if ((await db.drafts.get(inquiry.id)) !== undefined) continue

    const carried = inquiry.followUpSource.media
    const served = new Set(carried.map((medium) => medium.id))
    const stale = await db.media
      .where('inquiryId')
      .equals(inquiry.id)
      .filter((medium) => medium.carriedFrom != null && !served.has(medium.id))
      .primaryKeys()
    await db.media.bulkDelete(stale)

    for (const medium of carried) {
      if ((await db.media.get(medium.id)) !== undefined) continue

      let blob: Blob
      try {
        blob = await api.downloadMedium(accessToken, medium.id)
      } catch (error) {
        console.warn(
          `[sync] la pièce ${medium.id} de la contre-visite ${inquiry.id} n'a pas pu descendre — reprise à la passe suivante.`,
          error,
        )
        continue
      }

      await db.media.put({
        id: medium.id,
        inquiryId: inquiry.id,
        blob,
        type: medium.type,
        takenAt: medium.takenAt,
        label: medium.label,
        uploadedAt: new Date().toISOString(),
        carriedFrom: medium.carriedFrom,
      })
    }
  }
}

/**
 * Retire les pièces reprises des contre-visites que la tournée **complète** ne sert plus, quand
 * l'agent ne les a jamais ouvertes (#675) — une réaffectation, un abandon, une remise à zéro.
 *
 * Elles ne sont la saisie de personne : une copie du serveur, qui se redescend si la demande
 * revient. Gardées, ce seraient des photos de propriété privée sans terme sur la tablette, et une
 * « pièce » qui retiendrait la demande comme une saisie de l'agent — le ménage des demandes non
 * servies (#668) garde toute demande qui porte un média.
 *
 * ⚠️ **Avant ce ménage-là**, donc : une fois retirées, rien ne compte plus la demande comme
 * touchée. Une fiche ouverte garde ses pièces — elles sont alors à l'agent (#147).
 *
 * ⚠️ Sur une tournée **complète** seulement : une page qui manque porte peut-être la demande.
 * Seules les demandes candidates sont lues, jamais toutes les pièces de la tablette.
 */
export async function dropUnservedCarriedMedia(
  db: DataDatabase,
  served: ReadonlySet<string>,
): Promise<void> {
  await db.transaction('rw', [db.drafts, db.media], async () => {
    const opened = new Set(await db.drafts.toCollection().primaryKeys())
    const withMedia = (await db.media.orderBy('inquiryId').uniqueKeys()) as string[]
    const candidates = withMedia.filter((id) => !served.has(id) && !opened.has(id))
    if (candidates.length === 0) return

    const carried = await db.media
      .where('inquiryId')
      .anyOf(candidates)
      .filter((medium) => medium.carriedFrom != null)
      .primaryKeys()
    await db.media.bulkDelete(carried)
  })
}

/**
 * Reprend sur la tablette le **schéma corrigé au bureau** d'un contrôle renvoyé (#437) : le
 * brouillon de l'agent repart du dessin du bureau, et son image d'archive devient celle du bureau,
 * déjà dupliquée sous la clé de l'équipe — donc déjà envoyée. L'agent retouche ou retransmet tel
 * quel : dans les deux cas, ce qui part est le schéma courant.
 *
 * ⚠️ **Seulement sur un brouillon que l'agent n'a pas pu toucher** : verrouillé depuis sa dernière
 * transmission, et cette transmission rangée par le serveur (`sent`). Un brouillon déjà rouvert est
 * plus récent que la correction — l'agent y travaille —, et un envoi encore en file part tel qu'il
 * a été composé : dans les deux cas le plus récent est celui de l'agent, et il est gardé.
 *
 * ⚠️ **Une fois par correction** (`officeSchematicAt`) : la tournée sert la même tant que l'agent
 * n'a pas retransmis. Une image qui ne descend pas laisse le brouillon intact ; la passe suivante
 * reprend.
 *
 * ⚠️ La saisie transmise, elle, ne change pas (D4) : c'est le brouillon de la prochaine qui repart.
 */
export async function storeOfficeSchematics(
  db: DataDatabase,
  api: CarriedMediaApi,
  accessToken: string,
  inquiries: readonly AssignedInquiry[],
): Promise<void> {
  for (const inquiry of inquiries) {
    const corrected = inquiry.officeSchematic
    if (corrected === null) continue
    if (!(await rebaseable(db, inquiry.id, corrected.correctedAt))) continue

    let blob = (await db.media.get(corrected.mediumId))?.blob
    try {
      blob ??= await api.downloadMedium(accessToken, corrected.mediumId)
    } catch (error) {
      console.warn(
        `[sync] le schéma corrigé du contrôle ${inquiry.id} n'a pas pu descendre — reprise à la passe suivante.`,
        error,
      )
      continue
    }

    await db.transaction('rw', [db.drafts, db.media, db.outbox], async () => {
      // Relu sous la transaction : l'agent a pu rouvrir la fiche pendant le téléchargement.
      const draft = await db.drafts.get(inquiry.id)
      if (draft === undefined || !(await rebaseable(db, inquiry.id, corrected.correctedAt))) return

      const previous = await db.media
        .where('inquiryId')
        .equals(inquiry.id)
        .filter((medium) => medium.type === 'schematic' && medium.id !== corrected.mediumId)
        .primaryKeys()
      await db.media.bulkDelete(previous)
      await db.media.put({
        id: corrected.mediumId,
        inquiryId: inquiry.id,
        blob,
        type: 'schematic',
        takenAt: corrected.correctedAt,
        label: null,
        uploadedAt: new Date().toISOString(),
        sourceFingerprint: schematicFingerprint(corrected.schematic),
      })
      await db.drafts.put({
        ...draft,
        survey: { ...draft.survey, schematic: corrected.schematic },
        officeSchematicAt: corrected.correctedAt,
      })
    })
  }
}

/** Le brouillon peut-il repartir de cette correction sans perdre un geste de l'agent ? */
async function rebaseable(
  db: DataDatabase,
  inquiryId: string,
  correctedAt: string,
): Promise<boolean> {
  const draft = await db.drafts.get(inquiryId)
  if (draft === undefined || draft.lockedAt === null) return false
  if (draft.officeSchematicAt === correctedAt) return false
  // Un relevé v1 n'a pas de schéma à remplacer : il se monte en v2 à la transmission.
  if (draft.surveyVersion !== SURVEY_VERSION) return false

  return (await latestEntry(db, inquiryId))?.state === 'sent'
}
